import React, { createContext, useContext, useState, useEffect } from 'react';
import { notificationService } from '../services/apiServices';
import { useAuth } from './AuthContext';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (user) {
      fetchNotifications();
      fetchUnreadCount();
    }
  }, [user]);

  const fetchNotifications = async () => {
    try {
      const response = await notificationService.getUserNotifications();
      if (response.data.success) {
        setNotifications(response.data.data.notifications || []);
        // Don't update unread count here - fetch it separately for accuracy
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      // Use sample data for demo purposes
      setSampleNotifications();
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await notificationService.getUnreadCount();
      if (response.data.success) {
        setUnreadCount(response.data.data.unreadCount || 0);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
      // Keep existing unread count on error
    }
  };

  const setSampleNotifications = () => {
    const sampleNotifications = [
      {
        _id: '1',
        title: 'New Game Invitation',
        message: 'You have been invited to join "IPL 2024 Championship"',
        type: 'game_invitation',
        data: { gameId: '1', gameCode: 'IPL2024' },
        isRead: false,
        createdAt: new Date(Date.now() - 2 * 60 * 1000).toISOString(), // 2 minutes ago
      },
      {
        _id: '2',
        title: 'Match Results Updated',
        message: 'Points have been calculated for Match 15',
        type: 'match_result',
        data: { matchId: '15' },
        isRead: false,
        createdAt: new Date(Date.now() - 60 * 60 * 1000).toISOString(), // 1 hour ago
      },
      {
        _id: '3',
        title: 'Auction Starting Soon',
        message: 'Auction for "Weekend League" starts in 30 minutes',
        type: 'auction_reminder',
        data: { gameId: '3' },
        isRead: true,
        createdAt: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(), // 3 hours ago
      },
    ];
    setNotifications(sampleNotifications);
    setUnreadCount(sampleNotifications.filter(n => !n.isRead).length);
  };

  const markAsRead = async (notificationId) => {
    try {
      await notificationService.markAsRead(notificationId);
      setNotifications(prev =>
        prev.map(notification =>
          notification._id === notificationId
            ? { ...notification, isRead: true }
            : notification
        )
      );
      // Fetch updated unread count from server for accuracy
      fetchUnreadCount();
    } catch (error) {
      console.error('Error marking notification as read:', error);
      // Update locally for demo
      setNotifications(prev =>
        prev.map(notification =>
          notification._id === notificationId
            ? { ...notification, isRead: true }
            : notification
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
  };

  const markAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications(prev =>
        prev.map(notification => ({ ...notification, isRead: true }))
      );
      // Fetch updated unread count from server for accuracy
      fetchUnreadCount();
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      // Update locally for demo
      setNotifications(prev =>
        prev.map(notification => ({ ...notification, isRead: true }))
      );
      setUnreadCount(0);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await notificationService.deleteNotification(notificationId);
      const notification = notifications.find(n => n._id === notificationId);
      setNotifications(prev => prev.filter(n => n._id !== notificationId));
      // Refresh unread count if an unread notification was deleted
      if (notification && !notification.isRead) {
        fetchUnreadCount();
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
      // Update locally for demo
      const notification = notifications.find(n => n._id === notificationId);
      if (notification && !notification.isRead) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      setNotifications(prev => prev.filter(n => n._id !== notificationId));
    }
  };

  const addNotification = (notification) => {
    const newNotification = {
      _id: Date.now().toString(),
      ...notification,
      isRead: false,
      createdAt: new Date().toISOString(),
    };
    setNotifications(prev => [newNotification, ...prev]);
    setUnreadCount(prev => prev + 1);
  };

  const getTimeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    
    return date.toLocaleDateString();
  };

  const refreshNotifications = async () => {
    await fetchNotifications();
    await fetchUnreadCount();
  };

  const value = {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    addNotification,
    getTimeAgo,
    refreshNotifications,
    fetchUnreadCount,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};